import { useAction, useAtom } from '@reatom/npm-react'
import { useState } from 'react'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { SegmentControl } from '@blade/ui-kit'
import { Pair } from '@root/entities/pair'
import { PairListItem, PairListItemSkeleton } from '@root/entities/pair/ui'
import { toggleFavoritePairAction } from '@root/features/favorite-pairs'
import { recentPairsModelAtom } from '@root/features/trade/recent-trading-pairs'
import {
  tradingPairAddressAtom,
  tradingWalletAtom,
} from '@root/features/trade/trading'
import { trendingPairsStateAtom } from '@root/features/trending-pairs'
import { useTranslation } from '@root/shared/localization'
import { TradingList } from '@root/shared/trading'
import { Drawer, Input } from '@root/shared/ui-kit'

import type { PairModel } from '@root/entities/pair'
import type { DrawerControlProps } from '@root/shared/ui-kit'

type Tab = 'recent' | 'deluge-trending'

type Props = {
  onPairSelected?: (pair: Pair.PairMinimal) => void
} & DrawerControlProps

export const RecentPairsDrawer: React.FC<Props> = ({
  onPairSelected,
  open,
  onOpenChange,
}) => {
  const { t } = useTranslation()
  const [tab, setTab] = useState<Tab>('recent')
  const [trendingPairsState] = useAtom(trendingPairsStateAtom)
  const [activePair] = useAtom(tradingPairAddressAtom)

  const [recentPairsAtom] = useAtom(recentPairsModelAtom)
  const [pairsState] = useAtom(recentPairsAtom)
  const [wallet] = useAtom(tradingWalletAtom)

  const toggleFavoriteItem = useAction(toggleFavoritePairAction)

  const [searchQuery, setSearchQuery] = useState('')

  const handleInputChange = (value: string) => setSearchQuery(value)

  const filteredPairsState = AsyncStateN.map(pairsState, (pairs) =>
    pairs.filter(Pair.match(searchQuery)),
  )
  const filteredTrendingPairsState = AsyncStateN.map(
    trendingPairsState,
    (pairs) => pairs.filter(Pair.match(searchQuery)),
  )

  const renderContent = () => {
    if (tab === 'recent') {
      return (
        <TradingList<PairModel>
          getKey={(it) => it.address}
          emptyText={t('trading.search.recent-searches.empty')}
          renderItem={(it) => (
            <PairListItem
              key={it.address}
              pair={it}
              wallet={wallet}
              onClick={onPairSelected}
              active={it.address === activePair}
              onFavorite={(pair) => toggleFavoriteItem({ pair })}
            />
          )}
          renderSkeleton={() => <PairListItemSkeleton />}
          state={filteredPairsState}
          skeletonsCount={8}
        />
      )
    }

    return (
      <TradingList<PairModel>
        getKey={(it) => it.address}
        emptyText={t('trading.search.recent-searches.empty')}
        renderSkeleton={() => <PairListItemSkeleton />}
        state={filteredTrendingPairsState}
        skeletonsCount={8}
        renderItem={(it) => (
          <PairListItem
            key={it.address}
            pair={it}
            onClick={onPairSelected}
            active={it.address === activePair}
            wallet={wallet}
            onFavorite={(pair) => toggleFavoriteItem({ pair })}
          />
        )}
      />
    )
  }

  return (
    <Root
      title={t('trading.recent_pairs')}
      open={open}
      onOpenChange={onOpenChange}
      modal={false}
    >
      <Content>
        <Header>
          <SegmentControl value={tab} onChange={setTab}>
            <SegmentControl.Item value={'recent' satisfies Tab}>
              {t('trading.recent_pairs')}
            </SegmentControl.Item>
            <SegmentControl.Item value={'deluge-trending' satisfies Tab}>
              {t('trading.deluge_trending')}
            </SegmentControl.Item>
          </SegmentControl>
        </Header>
        <Search>
          <Input
            placeholder={t('wallet.search_by_name_address')}
            value={searchQuery}
            onChange={(e) => handleInputChange(e.target.value)}
            size="m"
          />
        </Search>
        {renderContent()}
      </Content>
    </Root>
  )
}

const Root = styled(Drawer)`
  height: var(--max-drawer-height);
`

const Content = styled.div`
  height: 100vh;
  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr);
  gap: 12px;
`
const Search = styled.div`
  padding: 0 12px;
`

const Header = styled.div`
  display: grid;
  justify-content: center;
`
